import { getApiUrl } from './platform';
import { getHomeownerToken, setHomeownerToken } from './storage';

function functionUrl(path) {
  return getApiUrl(path);
}

async function homeownerFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const token = Object.prototype.hasOwnProperty.call(options, 'token')
    ? options.token
    : await getHomeownerToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(functionUrl(path), { ...options, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || 'Request failed');
    error.status = response.status;
    error.body = data;
    throw error;
  }
  const renewed = renewedHomeownerToken(response, data);
  if (renewed) await setHomeownerToken(renewed);
  return data;
}

function isStorageJwt(value) {
  if (typeof value !== 'string') return false;
  const parts = value.split('.');
  if (parts.length !== 3) return false;
  // A three-part value is a Storage JWT. A long one still is, even with an empty segment.
  if (value.length > 40) return true;
  return parts.every((part) => part.length > 0);
}

function sessionToken(value) {
  if (typeof value !== 'string' || !value || isStorageJwt(value)) return '';
  return value;
}

function renewedHomeownerToken(response, data) {
  // A Storage signed-upload JWT is not a homeowner session. Saving it makes
  // the next write Unauthorized, and the resume check then signs them out.
  const fromBody = sessionToken(data && data.token);
  if (fromBody) return fromBody;
  if (response && response.headers && typeof response.headers.get === 'function') {
    const headerToken = response.headers.get('x-homeowner-token') || '';
    if (headerToken && !isStorageJwt(headerToken)) return headerToken;
  }
  return '';
}

let lastSessionTouch = 0;

/** App open / resume. API calls renew on their own through the response header. */
export async function touchHomeownerSession(now = Date.now()) {
  const before = await getHomeownerToken();
  if (!before) return null;
  if (now - lastSessionTouch < 60 * 1000) return null;
  lastSessionTouch = now;
  try {
    return await loadHomeownerClient();
  } catch (error) {
    if (error && error.status === 401) {
      const after = await getHomeownerToken();
      if (!after || after !== before) return null;
      return { unauthorized: true };
    }
    lastSessionTouch = 0;
    return null;
  }
}

export async function signupHomeowner(payload) {
  const data = await homeownerFetch('/.netlify/functions/homeowner-signup', {
    method: 'POST',
    body: JSON.stringify(payload),
    token: '',
  });
  if (sessionToken(data.token)) await setHomeownerToken(sessionToken(data.token));
  return data;
}

export async function loadHomeownerBilling() {
  return homeownerFetch('/.netlify/functions/homeowner-billing');
}

export async function startHomeownerCheckout(payload) {
  return homeownerFetch('/.netlify/functions/homeowner-billing', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function loginHomeowner(payload) {
  const data = await homeownerFetch('/.netlify/functions/homeowner-login', {
    method: 'POST',
    body: JSON.stringify(payload),
    token: '',
  });
  if (sessionToken(data.token)) await setHomeownerToken(sessionToken(data.token));
  return data;
}

export async function loadHomeownerClient() {
  return homeownerFetch('/.netlify/functions/homeowner-client');
}

export async function loadAgentBranding({ inviteCode, agentId } = {}) {
  return homeownerFetch('/.netlify/functions/agent-branding', {
    method: 'POST',
    body: JSON.stringify({ inviteCode, agentId }),
    token: '',
  });
}

export async function updateHomeownerClient(updates) {
  return homeownerFetch('/.netlify/functions/homeowner-client', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function acceptHomeownerTerms() {
  return homeownerFetch('/.netlify/functions/homeowner-client', {
    method: 'POST',
    body: JSON.stringify({ action: 'accept-terms', accepted: true }),
  });
}

export async function setHomeownerPassword(password, currentPassword, consent) {
  const body = { action: 'set-password', password };
  if (currentPassword) body.currentPassword = currentPassword;
  if (consent && consent.accepted === true) body.accepted = true;
  const data = await homeownerFetch('/.netlify/functions/homeowner-client', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (sessionToken(data.token)) await setHomeownerToken(sessionToken(data.token));
  return data;
}

export async function homeownerTasks(path = '', options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  const request = method === 'GET' && !options.cache
    ? { ...options, cache: 'no-store' }
    : options;
  return homeownerFetch(`/.netlify/functions/client-tasks${path}`, request);
}

export async function homeownerPost(path, body) {
  return homeownerFetch(path, { method: 'POST', body: JSON.stringify(body) });
}

export async function listHomeownerHomes() {
  return homeownerFetch('/.netlify/functions/homeowner-homes');
}

export async function moveHomeownerHome(fields) {
  const source = fields || {};
  const data = await homeownerFetch('/.netlify/functions/homeowner-homes', {
    method: 'POST',
    body: JSON.stringify({
      action: 'move',
      address: source.address,
      city: source.city,
      state: source.state,
      zip: source.zip,
      countyFips: source.countyFips || '',
      countySource: source.countySource || '',
      countyPending: !!source.countyPending,
      sponsor: source.sponsor,
      inviteCode: source.inviteCode || '',
    }),
  });
  if (sessionToken(data.token)) await setHomeownerToken(sessionToken(data.token));
  return data;
}

export async function acceptHomeownerSponsor(inviteCode) {
  return homeownerFetch('/.netlify/functions/homeowner-homes', {
    method: 'POST',
    body: JSON.stringify({ action: 'accept-sponsor', inviteCode }),
  });
}

export async function switchHomeownerHome(clientId) {
  const data = await homeownerFetch('/.netlify/functions/homeowner-homes', {
    method: 'POST',
    body: JSON.stringify({ action: 'switch', clientId }),
  });
  if (sessionToken(data.token)) await setHomeownerToken(sessionToken(data.token));
  return data;
}

export async function homeownerRecords(body) {
  return homeownerFetch('/.netlify/functions/homeowner-records', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function submitEnterpriseInquiry(fields) {
  return homeownerFetch('/.netlify/functions/enterprise-inquiry', {
    method: 'POST',
    body: JSON.stringify(fields),
    token: '',
  });
}

export async function ensureFriendLink() {
  return homeownerFetch('/.netlify/functions/homeowner-friend-link', {
    method: 'POST',
    body: JSON.stringify({ action: 'ensure' }),
  });
}

export async function resolveFriendLink(ref) {
  return homeownerFetch(`/.netlify/functions/friend-join?ref=${encodeURIComponent(ref || '')}`, {
    method: 'GET',
    token: '',
  });
}

export async function submitFriendJoin(payload) {
  const data = await homeownerFetch('/.netlify/functions/friend-join', {
    method: 'POST',
    body: JSON.stringify({ action: 'signup', ...payload }),
    token: '',
  });
  if (sessionToken(data.token)) await setHomeownerToken(sessionToken(data.token));
  return data;
}

export async function requestFriendSponsor(payload) {
  return homeownerFetch('/.netlify/functions/friend-join', {
    method: 'POST',
    body: JSON.stringify({ action: 'request', ...(payload || {}) }),
  });
}
